import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { prayerApi } from '@/lib/api';
import { Heart, Send, Check } from 'lucide-react';
import { toast } from 'sonner';

const PrayerRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    request: '',
    is_public: false
  });

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const data = await prayerApi.getAll(true);
      setRequests(data);
    } catch (error) {
      console.error('Error loading prayer requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.request) {
      toast.error('Please fill in your name and prayer request');
      return;
    }

    setSubmitting(true);
    try {
      await prayerApi.create(formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', request: '', is_public: false });
      toast.success('Prayer request submitted successfully');
      if (formData.is_public) {
        loadRequests();
      }
    } catch (error) {
      toast.error('Failed to submit prayer request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-700 to-amber-800 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Heart className="h-16 w-16 text-white mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            Prayer Requests
          </h1>
          <p className="text-xl text-amber-100 max-w-2xl mx-auto">
            Share your prayer needs with our church family. We believe in the power of prayer.
          </p>
        </div>
      </section>

      <section className="py-12 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Submit Form */}
            <div>
              <h2 className="text-2xl font-serif font-bold text-stone-800 mb-6">
                Submit a Prayer Request
              </h2>
              
              {submitted ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-stone-800 mb-2">
                      Prayer Request Submitted
                    </h3>
                    <p className="text-stone-600 mb-4">
                      Thank you for sharing your prayer request. Our church family will be praying for you.
                    </p>
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      Submit Another Request
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="name">Your Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Enter your name"
                          required
                          data-testid="prayer-name-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="email">Email (optional)</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Enter your email"
                          data-testid="prayer-email-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="request">Prayer Request *</Label>
                        <Textarea
                          id="request"
                          value={formData.request}
                          onChange={(e) => setFormData({ ...formData, request: e.target.value })}
                          placeholder="Share your prayer request..."
                          rows={5}
                          required
                          data-testid="prayer-request-input"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="is_public"
                          checked={formData.is_public}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                          data-testid="prayer-public-checkbox"
                        />
                        <Label htmlFor="is_public" className="text-sm text-stone-600">
                          Allow this prayer request to be shared publicly (after approval)
                        </Label>
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full bg-amber-700 hover:bg-amber-800"
                        disabled={submitting}
                        data-testid="prayer-submit-btn"
                      >
                        {submitting ? (
                          'Submitting...'
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            Submit Prayer Request
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Public Requests */}
            <div>
              <h2 className="text-2xl font-serif font-bold text-stone-800 mb-6">
                Community Prayer Requests
              </h2>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto"></div>
                </div>
              ) : requests.length > 0 ? (
                <div className="space-y-4">
                  {requests.map((request) => (
                    <Card key={request.id} data-testid="prayer-request-card">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Heart className="h-5 w-5 text-amber-700 mt-1 flex-shrink-0" />
                          <div>
                            <p className="text-stone-700 mb-2">{request.request}</p>
                            <div className="flex items-center gap-2 text-sm text-stone-500">
                              <span>{request.name}</span>
                              <span>•</span>
                              <span>{formatDate(request.created_at)}</span>
                              {request.is_answered && (
                                <span className="text-green-600 font-medium ml-2">
                                  ✓ Answered
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-6 text-center">
                    <Heart className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                    <p className="text-stone-600">No public prayer requests at this time.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Scripture */}
      <section className="py-12 bg-amber-50">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <blockquote className="text-xl italic text-stone-700 mb-4">
            "Therefore I tell you, whatever you ask for in prayer, believe that you have received it, and it will be yours."
          </blockquote>
          <cite className="text-amber-700 font-medium">- Mark 11:24</cite>
        </div>
      </section>
    </Layout>
  );
};

export default PrayerRequestsPage;
